import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { useChat } from '../hooks/useChat';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { enviarMensagemChat, extractPatientIdFromConversationId } from '../services/dataService';
import { database } from '../config/backend';
import { ref, get as rtdbGet } from '../services/phpDataAdapter';
import { generateMealPlanPDF } from '../utils/mealPlanPdfExport';
import { sendPDFToPatient } from '../services/pdfSendService';
import { uploadAudio } from '../services/audioUploadService';
import { uploadImage } from '../services/imageUploadService';
import { uploadVideo } from '../services/videoUploadService';
import { useHasVideoAccess } from '../hooks/useHasVideoAccess';
import { VideoRecorder } from '../components/video/VideoRecorder';
import { VideoPlayer } from '../components/video/VideoPlayer';
import { LiveStream } from '../components/video/LiveStream';
import './Pages.css';
import './ChatEnhancements.css';

interface Plano {
  id: string;
  nome: string;
  pacienteId: string;
  atualizadoEm: string;
}

interface MealPlan {
  id: string;
  name: string;
  createdAt: string | undefined;
}

export const Chat: React.FC = () => {
  const { user } = useAuth();
  const { theme, isDark: isDarkMode } = useTheme();
  const [conversaIdSelecionada, setConversaIdSelecionada] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState('');
  const { conversas, mensagens, loading, error, messagesLoading, messagesError } = useChat(conversaIdSelecionada);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [loadingPlanos, setLoadingPlanos] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [sendingPDF, setSendingPDF] = useState(false);
  const [pdfMessage, setPdfMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLDivElement>(null);

  // Estados para gravação de áudio
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Estados para envio de imagem
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados para envio de vídeo
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [showLiveStream, setShowLiveStream] = useState(false);
  const { hasAccess: hasVideoAccess } = useHasVideoAccess();

  // Estados para ficha do paciente
  const [showPatientRecord, setShowPatientRecord] = useState(false);
  const [patientData, setPatientData] = useState<any>(null);
  const [loadingPatientData, setLoadingPatientData] = useState(false);

  const conversaAtual = useMemo(
    () => conversas.find((c) => c.id === conversaIdSelecionada) || null,
    [conversas, conversaIdSelecionada]
  );

  // Scroll automático para última mensagem
  useEffect(() => {
    if (messagesEndRef.current) {
      // Usar setTimeout para garantir que o DOM seja atualizado antes do scroll
      setTimeout(() => {
        if (messagesEndRef.current && chatInputRef.current) {
          // Fazer scroll para a última mensagem, mas garantir que o input fique visível
          const container = messagesEndRef.current.closest('.ns-chat-messages');
          if (container) {
            const containerElement = container as HTMLElement;
            const inputHeight = chatInputRef.current.offsetHeight;
            const scrollHeight = containerElement.scrollHeight;
            const clientHeight = containerElement.clientHeight;

            // Calcular a posição de scroll para manter o input visível
            const maxScroll = scrollHeight - clientHeight;
            const targetScroll = Math.min(maxScroll, scrollHeight - inputHeight - 20);

            containerElement.scrollTo({
              top: targetScroll,
              behavior: 'smooth'
            });
          } else {
            // Fallback: usar scrollIntoView se não encontrar o container
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }, 150);
    }
  }, [mensagens]);

  const handleSelectConversa = (id: string) => {
    setConversaIdSelecionada(id);
  };

  const handleSend = async () => {
    if (!conversaIdSelecionada || !mensagem.trim()) return;
    await enviarMensagemChat(conversaIdSelecionada, 'nutritionist', mensagem);
    setMensagem('');
  };

  // Função para iniciar gravação de áudio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await sendAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      // Iniciar contador de duração
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Erro ao iniciar gravação:', error);
      alert('Não foi possível iniciar a gravação. Verifique as permissões do microfone.');
    }
  };

  // Função para parar gravação
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  // Função para cancelar gravação
  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      audioChunksRef.current = [];

      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  // Função para enviar áudio
  const sendAudio = async (audioBlob: Blob) => {
    if (!conversaIdSelecionada || !user?.uid) return;

    try {
      setUploadingAudio(true);

      // Converter blob para File
      const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });

      // Fazer upload
      const { url: audioUrl } = await uploadAudio(audioFile, user.uid);

      // Enviar mensagem com áudio
      await enviarMensagemChat(conversaIdSelecionada, 'nutritionist', '', audioUrl);

      setRecordingDuration(0);
    } catch (error: any) {
      console.error('Erro ao enviar áudio:', error);
      alert(error.message || 'Não foi possível enviar o áudio. Tente novamente.');
    } finally {
      setUploadingAudio(false);
    }
  };

  // Função para selecionar e enviar imagem
  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!conversaIdSelecionada || !user?.uid) return;

    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione um arquivo de imagem.');
      return;
    }

    // Validar tamanho (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('A imagem deve ter no máximo 5MB.');
      return;
    }

    try {
      setUploadingImage(true);

      // Fazer upload
      const { url: imageUrl } = await uploadImage(file, user.uid, 'chat-images');

      // Enviar mensagem com imagem
      await enviarMensagemChat(conversaIdSelecionada, 'nutritionist', '', undefined, imageUrl);

      // Limpar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Erro ao enviar imagem:', error);
      alert(error.message || 'Não foi possível enviar a imagem. Tente novamente.');
    } finally {
      setUploadingImage(false);
    }
  };

  // Função para processar vídeo gravado
  const handleVideoRecordComplete = async (videoBlob: Blob, thumbnail?: string) => {
    if (!conversaIdSelecionada || !user?.uid) return;

    try {
      setUploadingVideo(true);
      setShowVideoRecorder(false);

      // Converter blob para File
      const videoFile = new File([videoBlob], `video_${Date.now()}.webm`, { type: 'video/webm' });

      // Fazer upload
      const { url: videoUrl, thumbnailUrl } = await uploadVideo(videoFile, user.uid, 'chat-videos');

      // Enviar mensagem com vídeo
      await enviarMensagemChat(conversaIdSelecionada, 'nutritionist', '', undefined, undefined, videoUrl, thumbnailUrl);
    } catch (error: any) {
      console.error('Erro ao processar vídeo:', error);
      alert(error.message || 'Não foi possível processar o vídeo. Tente novamente.');
    } finally {
      setUploadingVideo(false);
    }
  };

  // Limpar ao desmontar
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  // Fechar modal lateral com tecla ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showPatientRecord) {
        setShowPatientRecord(false);
        setPatientData(null);
      }
    };

    if (showPatientRecord) {
      window.addEventListener('keydown', handleEscape);
      return () => {
        window.removeEventListener('keydown', handleEscape);
      };
    }
  }, [showPatientRecord]);

  const handleOpenPlanModal = async () => {
    if (!conversaIdSelecionada || !user?.uid) return;

    const patientId = extractPatientIdFromConversationId(conversaIdSelecionada, user.uid);
    if (!patientId) {
      alert('Não foi possível identificar o paciente.');
      return;
    }

    setShowPlanModal(true);
    setLoadingPlanos(true);
    setPlanos([]);
    setSelectedPlan(null);
    setPdfMessage('');

    try {
      const plansRef = ref(database, `mealPlans/${patientId}`);
      const snapshot = await rtdbGet(plansRef);

      if (snapshot.exists()) {
        const plansData = snapshot.val() as Record<string, any>;
        const plansList: Plano[] = Object.entries(plansData).map(([id, data]) => ({
          id,
          nome: data.nome || data.title || data.name || 'Plano Alimentar',
          pacienteId: patientId,
          atualizadoEm: data.updatedAt || data.createdAt || new Date().toISOString(),
        }));

        // Ordenar por data de atualização (mais recente primeiro)
        plansList.sort((a, b) =>
          new Date(b.atualizadoEm).getTime() - new Date(a.atualizadoEm).getTime()
        );

        setPlanos(plansList);
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      alert('Erro ao carregar planos do paciente.');
    } finally {
      setLoadingPlanos(false);
    }
  };

  const handleSendPlanPDF = async () => {
    if (!selectedPlan || !conversaIdSelecionada || !user?.uid) return;

    const patientId = extractPatientIdFromConversationId(conversaIdSelecionada, user.uid);
    if (!patientId) {
      alert('Não foi possível identificar o paciente.');
      return;
    }

    setSendingPDF(true);
    try {
      // Buscar dados completos do plano
      const planRef = ref(database, `mealPlans/${patientId}/${selectedPlan}`);
      const snapshot = await rtdbGet(planRef);

      if (!snapshot.exists()) {
        alert('Plano não encontrado');
        return;
      }

      const planData = snapshot.val();

      // Buscar nome do paciente
      const userSnap = await rtdbGet(ref(database, `users/${patientId}`));
      const patientName = userSnap.exists()
        ? (userSnap.val() as any).name || 'Paciente'
        : 'Paciente';

      const nutritionistName = user.email || 'Nutricionista';

      // Suportar ambos os formatos (web e mobile)
      // Nome do plano
      const planName = planData.name || planData.title || planData.nome || 'Plano Alimentar';
      
      // Objetivo
      const planGoal = planData.goal || planData.objetivo || '';
      
      // Observações
      const planNotes = planData.notes || planData.observacoes || '';
      
      // Calorias e macronutrientes
      let targetCalories = planData.targetCalories || planData.dailyCalories || null;
      let protein = planData.protein || null;
      let carbohydrates = planData.carbohydrates || null;
      let fats = planData.fats || null;
      
      // Se os macronutrientes estiverem em uma estrutura nested
      if (planData.macronutrients) {
        protein = planData.macronutrients.protein || protein;
        carbohydrates = planData.macronutrients.carbohydrates || carbohydrates;
        fats = planData.macronutrients.fats || fats;
      }
      
      // Hidratação
      const hydration = planData.hydration || '2L/dia';
      
      // Plano semanal - verificar se existe weeklyPlan ou meals
      let weeklyPlan = planData.weeklyPlan || null;
      
      // Se não houver weeklyPlan mas houver meals (estrutura do mobile), converter
      if (!weeklyPlan && planData.meals) {
        const meals = planData.meals;
        // Converter estrutura meals para weeklyPlan (replicar para todos os dias)
        weeklyPlan = {
          monday: {
            breakfast: Array.isArray(meals.breakfast) ? meals.breakfast : [],
            morningSnack: Array.isArray(meals.morningSnack) ? meals.morningSnack : [],
            lunch: Array.isArray(meals.lunch) ? meals.lunch : [],
            afternoonSnack: Array.isArray(meals.afternoonSnack) ? meals.afternoonSnack : [],
            dinner: Array.isArray(meals.dinner) ? meals.dinner : [],
            eveningSnack: Array.isArray(meals.eveningSnack) ? meals.eveningSnack : [],
          },
          tuesday: {
            breakfast: Array.isArray(meals.breakfast) ? meals.breakfast : [],
            morningSnack: Array.isArray(meals.morningSnack) ? meals.morningSnack : [],
            lunch: Array.isArray(meals.lunch) ? meals.lunch : [],
            afternoonSnack: Array.isArray(meals.afternoonSnack) ? meals.afternoonSnack : [],
            dinner: Array.isArray(meals.dinner) ? meals.dinner : [],
            eveningSnack: Array.isArray(meals.eveningSnack) ? meals.eveningSnack : [],
          },
          wednesday: {
            breakfast: Array.isArray(meals.breakfast) ? meals.breakfast : [],
            morningSnack: Array.isArray(meals.morningSnack) ? meals.morningSnack : [],
            lunch: Array.isArray(meals.lunch) ? meals.lunch : [],
            afternoonSnack: Array.isArray(meals.afternoonSnack) ? meals.afternoonSnack : [],
            dinner: Array.isArray(meals.dinner) ? meals.dinner : [],
            eveningSnack: Array.isArray(meals.eveningSnack) ? meals.eveningSnack : [],
          },
          thursday: {
            breakfast: Array.isArray(meals.breakfast) ? meals.breakfast : [],
            morningSnack: Array.isArray(meals.morningSnack) ? meals.morningSnack : [],
            lunch: Array.isArray(meals.lunch) ? meals.lunch : [],
            afternoonSnack: Array.isArray(meals.afternoonSnack) ? meals.afternoonSnack : [],
            dinner: Array.isArray(meals.dinner) ? meals.dinner : [],
            eveningSnack: Array.isArray(meals.eveningSnack) ? meals.eveningSnack : [],
          },
          friday: {
            breakfast: Array.isArray(meals.breakfast) ? meals.breakfast : [],
            morningSnack: Array.isArray(meals.morningSnack) ? meals.morningSnack : [],
            lunch: Array.isArray(meals.lunch) ? meals.lunch : [],
            afternoonSnack: Array.isArray(meals.afternoonSnack) ? meals.afternoonSnack : [],
            dinner: Array.isArray(meals.dinner) ? meals.dinner : [],
            eveningSnack: Array.isArray(meals.eveningSnack) ? meals.eveningSnack : [],
          },
          saturday: {
            breakfast: Array.isArray(meals.breakfast) ? meals.breakfast : [],
            morningSnack: Array.isArray(meals.morningSnack) ? meals.morningSnack : [],
            lunch: Array.isArray(meals.lunch) ? meals.lunch : [],
            afternoonSnack: Array.isArray(meals.afternoonSnack) ? meals.afternoonSnack : [],
            dinner: Array.isArray(meals.dinner) ? meals.dinner : [],
            eveningSnack: Array.isArray(meals.eveningSnack) ? meals.eveningSnack : [],
          },
          sunday: {
            breakfast: Array.isArray(meals.breakfast) ? meals.breakfast : [],
            morningSnack: Array.isArray(meals.morningSnack) ? meals.morningSnack : [],
            lunch: Array.isArray(meals.lunch) ? meals.lunch : [],
            afternoonSnack: Array.isArray(meals.afternoonSnack) ? meals.afternoonSnack : [],
            dinner: Array.isArray(meals.dinner) ? meals.dinner : [],
            eveningSnack: Array.isArray(meals.eveningSnack) ? meals.eveningSnack : [],
          },
        };
      }

      const pdfData = {
        name: planName,
        goal: planGoal,
        notes: planNotes,
        targetCalories: targetCalories,
        protein: protein,
        carbohydrates: carbohydrates,
        fats: fats,
        weeklyPlan: weeklyPlan,
        hydration: hydration,
        trainingTime: planData.trainingTime,
        substitutions: planData.substitutions || null,
      };

      const blob = await generateMealPlanPDF(pdfData, patientName, nutritionistName);
      const fileName = `plano_alimentar_${patientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;

      await sendPDFToPatient(patientId, user.uid, 'mealPlan', fileName, blob, pdfMessage || undefined, true);

      alert('PDF do plano enviado com sucesso!');
      setShowPlanModal(false);
      setSelectedPlan(null);
      setPdfMessage('');
    } catch (error) {
      console.error('Erro ao enviar PDF:', error);
      alert('Erro ao enviar PDF. Tente novamente.');
    } finally {
      setSendingPDF(false);
    }
  };

  const handleViewPatientRecord = async () => {
    if (!conversaIdSelecionada || !user?.uid) return;

    const patientId = extractPatientIdFromConversationId(conversaIdSelecionada, user.uid);
    if (!patientId) {
      alert('Não foi possível identificar o paciente.');
      return;
    }

    setLoadingPatientData(true);
    setShowPatientRecord(true);
    setPatientData(null);

    try {
      // Buscar dados completos do paciente
      const patientRef = ref(database, `users/${patientId}`);
      const patientSnap = await rtdbGet(patientRef);

      if (!patientSnap.exists()) {
        alert('Dados do paciente não encontrados.');
        setShowPatientRecord(false);
        return;
      }

      const patientInfo = patientSnap.val();

      // Buscar medições mais recentes
      let latestMeasurements = null;
      try {
        const measurementsRef = ref(database, `measurements/${patientId}`);
        const measurementsSnap = await rtdbGet(measurementsRef);
        
        if (measurementsSnap.exists()) {
          const measurements = measurementsSnap.val();
          const measurementsList = Object.values(measurements) as any[];
          if (measurementsList.length > 0) {
            // Ordenar por data e pegar a mais recente
            measurementsList.sort((a, b) => {
              const dateA = new Date(a.date || a.createdAt || 0).getTime();
              const dateB = new Date(b.date || b.createdAt || 0).getTime();
              return dateB - dateA;
            });
            latestMeasurements = measurementsList[0];
          }
        }
      } catch (error) {
        console.error('Erro ao buscar medições:', error);
      }

      // Buscar planos alimentares
      let mealPlans: MealPlan[] = [];
      try {
        const plansRef = ref(database, `mealPlans/${patientId}`);
        const plansSnap = await rtdbGet(plansRef);
        
        if (plansSnap.exists()) {
          const plans = plansSnap.val();
          mealPlans = Object.entries(plans).map(([id, data]: [string, any]) => ({
            id,
            name: data.name || data.title || 'Plano Alimentar',
            createdAt: data.createdAt || data.updatedAt,
          }));
          mealPlans.sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateB - dateA;
          });
        }
      } catch (error) {
        console.error('Erro ao buscar planos:', error);
      }

      setPatientData({
        ...patientInfo,
        latestMeasurements,
        mealPlans,
      });
    } catch (error) {
      console.error('Erro ao carregar dados do paciente:', error);
      alert('Erro ao carregar dados do paciente.');
      setShowPatientRecord(false);
    } finally {
      setLoadingPatientData(false);
    }
  };

  return (
    <div className="ns-page">
      {/* Overlay para fechar o modal lateral */}
      {showPatientRecord && (
        <div
          className="ns-patient-overlay"
          onClick={() => {
            setShowPatientRecord(false);
            setPatientData(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 998,
            cursor: 'pointer',
            animation: 'fadeIn 0.2s ease-in'
          }}
        />
      )}

      <div className="ns-page-header">
        <div>
          <h2>Chat com pacientes</h2>
          <p>
            Centralize a comunicação com seus pacientes em um único
            lugar.
          </p>
        </div>
      </div>

      <div className="ns-chat-layout" style={{ display: 'flex', gap: '16px', position: 'relative' }}>
        <Card
          title="Conversas"
          subtitle="Lista de pacientes vinculados ao nutricionista"
          style={{ flex: showPatientRecord ? '0 0 280px' : '0 0 280px' }}
        >
          {loading ? (
            <p>Carregando conversas...</p>
          ) : error ? (
            <p role="alert">{error}</p>
          ) : (
            <ul className="ns-chat-list">
              {conversas.map((c) => (
                <li
                  key={c.id}
                  className={
                    conversaIdSelecionada === c.id ? 'active' : undefined
                  }
                  onClick={() => handleSelectConversa(c.id)}
                >
                  <span className="name">{c.pacienteNome}</span>
                  <span className="preview">{c.resumo}</span>
                </li>
              ))}
              {conversas.length === 0 && (
                <li>
                  <span className="name">Nenhuma conversa ainda</span>
                  <span className="preview">
                    Vincule pacientes pelo app para iniciar conversas.
                  </span>
                </li>
              )}
            </ul>
          )}
        </Card>

        <Card
          title={conversaAtual?.pacienteNome || 'Selecione uma conversa'}
          subtitle={
            conversaAtual
              ? 'Paciente em acompanhamento'
              : 'Escolha um paciente na lista ao lado'
          }
          actions={
            conversaAtual ? (
              <>
                <Button variant="ghost" onClick={handleOpenPlanModal}>
                  📄 Enviar PDF do Plano
                </Button>
                <Button variant="ghost" onClick={handleViewPatientRecord}>
                  Ver ficha
                </Button>
              </>
            ) : undefined
          }
          style={{ flex: showPatientRecord ? '1 1 50%' : '1 1 auto', minWidth: 0 }}
        >
          <div className="ns-chat-window">
            <div className="ns-chat-messages">
              {conversaIdSelecionada == null && (
                <p className="ns-chat-empty">
                  Selecione uma conversa para visualizar as mensagens.
                </p>
              )}
              {conversaIdSelecionada != null && messagesLoading && (
                <p className="ns-chat-empty" role="status">Carregando mensagens...</p>
              )}
              {conversaIdSelecionada != null && messagesError && (
                <p className="ns-chat-empty" role="alert">{messagesError}</p>
              )}
              {conversaIdSelecionada != null && !messagesLoading && !messagesError && mensagens.length === 0 && (
                <p className="ns-chat-empty" role="status">
                  Esta conversa ainda não tem mensagens. Envie uma mensagem para iniciar o atendimento.
                </p>
              )}
              {conversaIdSelecionada != null && !messagesLoading && !messagesError &&
                mensagens.map((m) => (
                  <div
                    key={m.id}
                    className={
                      m.autor === 'nutri'
                        ? 'ns-chat-message sent'
                        : 'ns-chat-message received'
                    }
                  >
                    {(m.video || m.videoUrl) ? (
                      <div style={{ marginBottom: '8px', maxWidth: '400px' }}>
                        <VideoPlayer
                          videoUrl={m.video || m.videoUrl || ''}
                          thumbnailUrl={m.videoThumbnail}
                          controls
                        />
                      </div>
                    ) : m.imagem || m.image ? (
                      <div style={{ marginBottom: '8px' }}>
                        <img
                          src={m.imagem || m.image}
                          alt="Imagem enviada"
                          style={{
                            maxWidth: '300px',
                            maxHeight: '300px',
                            borderRadius: '8px',
                            objectFit: 'cover',
                          }}
                        />
                      </div>
                    ) : m.audio ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <audio controls style={{ maxWidth: '250px' }}>
                          <source src={m.audio} type="audio/webm" />
                          <source src={m.audio} type="audio/mpeg" />
                          <source src={m.audio} type="audio/wav" />
                          Seu navegador não suporta áudio.
                        </audio>
                        <span style={{ fontSize: '12px', opacity: 0.7 }}>🎤</span>
                      </div>
                    ) : (
                      <div>
                        {m.type === 'pdf' && m.pdfId ? (
                          <div style={{ marginBottom: '8px' }}>
                            <a
                              href={`/pdf/${m.pdfId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: theme.colors.surface,
                                borderRadius: '8px',
                                textDecoration: 'none',
                                color: 'var(--theme-link)',
                                fontWeight: '500',
                                border: `1px solid ${theme.colors.border}`
                              }}
                            >
                              <span>📄</span>
                              <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span>Ver PDF</span>
                                {m.pdfFileName && (
                                  <span style={{ fontSize: '12px', opacity: 0.7, fontWeight: '400' }}>
                                    {m.pdfFileName}
                                  </span>
                                )}
                              </span>
                            </a>
                          </div>
                        ) : null}
                        <p style={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {m.conteudo?.split(/(https?:\/\/[^\s]+)/g).map((part, i) => {
                            if (part.match(/^https?:\/\//)) {
                              return (
                                <a
                                  key={i}
                                  href={part}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: 'var(--theme-link)', textDecoration: 'underline' }}
                                >
                                  {part}
                                </a>
                              );
                            }
                            return part;
                          })}
                        </p>
                      </div>
                    )}
                    {m.horario && (
                      <span className="time">{m.horario}</span>
                    )}
                  </div>
                ))}
              <div ref={messagesEndRef} />
            </div>
            <div ref={chatInputRef} className="ns-chat-input" style={{ flexDirection: 'column', gap: '8px' }}>
              {isRecording && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px',
                  backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
                  borderRadius: '8px',
                  width: '100%'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: 'var(--theme-danger-solid)',
                    animation: 'recordingPulse 1s infinite'
                  }} />
                  <span style={{ flex: 1, fontSize: '14px', color: isDarkMode ? '#fca5a5' : '#991b1b' }}>
                    Gravando... {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={cancelRecording}
                    disabled={uploadingAudio}
                    style={{ marginRight: '4px' }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    onClick={stopRecording}
                    disabled={uploadingAudio}
                  >
                    {uploadingAudio ? 'Enviando...' : 'Enviar'}
                  </Button>
                </div>
              )}
              {uploadingImage && !isRecording && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px',
                  backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: isDarkMode ? '#93c5fd' : '#1e40af',
                  width: '100%'
                }}>
                  <span>⏳</span>
                  <span>Enviando imagem...</span>
                </div>
              )}
              {uploadingVideo && !isRecording && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px',
                  backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: isDarkMode ? '#93c5fd' : '#1e40af',
                  width: '100%'
                }}>
                  <span>⏳</span>
                  <span>Enviando vídeo...</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'nowrap' }}>
                {conversaIdSelecionada && conversaAtual && (
                  <Button
                    type="button"
                    onClick={() => {
                      if (user?.uid) {
                        const patientId = extractPatientIdFromConversationId(conversaIdSelecionada!, user.uid);
                        window.location.href = `/video-call?recipientId=${patientId}&recipientName=${encodeURIComponent(conversaAtual.pacienteNome || 'Paciente')}`;
                      }
                    }}
                    style={{ marginRight: '8px', flexShrink: 0 }}
                    title="Iniciar Videochamada"
                  >
                    📹
                  </Button>
                )}
                {!isRecording && hasVideoAccess && (
                  <>
                    <Button
                      type="button"
                      onClick={() => {
                        if (hasVideoAccess) {
                          setShowVideoRecorder(true);
                        } else {
                          alert('Esta funcionalidade está disponível apenas para plano intermediário ou superior. Faça upgrade do seu plano.');
                        }
                      }}
                      disabled={uploadingVideo || uploadingImage}
                      style={{ marginRight: '8px', flexShrink: 0 }}
                      title="Gravar Vídeo"
                    >
                      {uploadingVideo ? '⏳' : '🎥'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        if (hasVideoAccess) {
                          setShowLiveStream(true);
                        } else {
                          alert('Esta funcionalidade está disponível apenas para plano intermediário ou superior. Faça upgrade do seu plano.');
                        }
                      }}
                      disabled={uploadingVideo || uploadingImage}
                      style={{ marginRight: '8px', flexShrink: 0 }}
                      title="Transmissão Ao Vivo"
                    >
                      🔴
                    </Button>
                  </>
                )}
                {!isRecording && (
                  <>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                    />
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage || uploadingVideo}
                      style={{ marginRight: '8px', flexShrink: 0 }}
                      title="Enviar Imagem"
                    >
                      {uploadingImage ? '⏳' : '📷'}
                    </Button>
                  </>
                )}
                {!isRecording && (
                  <Button
                    type="button"
                    onClick={startRecording}
                    onMouseDown={startRecording}
                    style={{ marginRight: '8px', flexShrink: 0 }}
                    title="Gravar Áudio"
                  >
                    🎤
                  </Button>
                )}
                {!isRecording && (
                  <input
                    className="ns-input"
                    placeholder={
                      conversaIdSelecionada
                        ? 'Digite uma mensagem...'
                        : 'Selecione uma conversa para enviar mensagens'
                    }
                    value={mensagem}
                    onChange={(e) => setMensagem(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    disabled={!conversaIdSelecionada || uploadingImage}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                )}
                {!isRecording && (
                  <Button
                    type="button"
                    onClick={handleSend}
                    disabled={!conversaIdSelecionada || !mensagem.trim() || uploadingImage}
                    style={{ flexShrink: 0 }}
                  >
                    Enviar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Painel Lateral da Ficha do Paciente */}
        {showPatientRecord && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ flex: '0 0 400px', maxWidth: '400px', zIndex: 999 }}
          >
            <Card
              title="Ficha do Paciente"
              subtitle={conversaAtual?.pacienteNome || 'Paciente'}
              className="ns-patient-info-card"
              style={{
                maxWidth: '400px',
                maxHeight: 'calc(100vh - 200px)',
                overflowY: 'auto',
                position: 'sticky',
                top: '16px',
                alignSelf: 'flex-start',
                cursor: 'default'
              }}
              actions={
                <Button
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPatientRecord(false);
                    setPatientData(null);
                  }}
                  style={{ padding: '4px 8px', cursor: 'pointer' }}
                  title="Fechar (ESC)"
                >
                  ✕
                </Button>
              }
            >
            {loadingPatientData ? (
              <div className="ns-patient-loading">
                <p>Carregando dados do paciente</p>
              </div>
            ) : patientData ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Dados Pessoais */}
                <div className="ns-patient-info-section">
                  <h3>Dados Pessoais</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                    <div className="ns-patient-info-row">
                      <span className="ns-patient-info-label">Nome:</span>
                      <span className="ns-patient-info-value">{patientData.name || patientData.nome || '-'}</span>
                    </div>
                    {patientData.email && (
                      <div className="ns-patient-info-row">
                        <span className="ns-patient-info-label">Email:</span>
                        <span className="ns-patient-info-value">{patientData.email}</span>
                      </div>
                    )}
                    {patientData.phone && (
                      <div className="ns-patient-info-row">
                        <span className="ns-patient-info-label">Telefone:</span>
                        <span className="ns-patient-info-value">{patientData.phone}</span>
                      </div>
                    )}
                    {patientData.birthDate && (
                      <div className="ns-patient-info-row">
                        <span className="ns-patient-info-label">Data de Nascimento:</span>
                        <span className="ns-patient-info-value">
                          {new Date(patientData.birthDate).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    )}
                    {patientData.gender && (
                      <div className="ns-patient-info-row">
                        <span className="ns-patient-info-label">Gênero:</span>
                        <span className="ns-patient-info-value">
                          {patientData.gender === 'male' ? 'Masculino' : patientData.gender === 'female' ? 'Feminino' : patientData.gender}
                        </span>
                      </div>
                    )}
                    {patientData.height && (
                      <div className="ns-patient-info-row">
                        <span className="ns-patient-info-label">Altura:</span>
                        <span className="ns-patient-info-value">{patientData.height} cm</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Medições Recentes */}
                {patientData.latestMeasurements && (
                  <div className="ns-patient-info-section">
                    <h3>Medições Recentes</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                      {patientData.latestMeasurements.weight && (
                        <div className="ns-patient-info-row">
                          <span className="ns-patient-info-label">Peso:</span>
                          <span className="ns-patient-info-value">{patientData.latestMeasurements.weight} kg</span>
                        </div>
                      )}
                      {patientData.latestMeasurements.waist && (
                        <div className="ns-patient-info-row">
                          <span className="ns-patient-info-label">Cintura:</span>
                          <span className="ns-patient-info-value">{patientData.latestMeasurements.waist} cm</span>
                        </div>
                      )}
                      {patientData.latestMeasurements.hip && (
                        <div className="ns-patient-info-row">
                          <span className="ns-patient-info-label">Quadril:</span>
                          <span className="ns-patient-info-value">{patientData.latestMeasurements.hip} cm</span>
                        </div>
                      )}
                      {patientData.latestMeasurements.bodyFat && (
                        <div className="ns-patient-info-row">
                          <span className="ns-patient-info-label">Gordura Corporal:</span>
                          <span className="ns-patient-info-value">{patientData.latestMeasurements.bodyFat}%</span>
                        </div>
                      )}
                      {patientData.latestMeasurements.date && (
                        <div className="ns-patient-info-row" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                          <span className="ns-patient-info-label" style={{ fontSize: '12px' }}>Data:</span>
                          <span className="ns-patient-info-value" style={{ fontSize: '12px' }}>
                            {new Date(patientData.latestMeasurements.date).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Planos Alimentares */}
                {patientData.mealPlans && patientData.mealPlans.length > 0 && (
                  <div className="ns-patient-info-section">
                    <h3>
                      Planos Alimentares
                      <span className="ns-info-badge">{patientData.mealPlans.length}</span>
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                      {patientData.mealPlans.slice(0, 5).map((plan: any) => (
                        <div key={plan.id} className="ns-meal-plan-card">
                          <div className="ns-meal-plan-name">{plan.name}</div>
                          {plan.createdAt && (
                            <div className="ns-meal-plan-date">
                              {new Date(plan.createdAt).toLocaleDateString('pt-BR')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Condições Médicas */}
                {(patientData.medicalConditions || patientData.allergies || patientData.medications) && (
                  <div className="ns-patient-info-section">
                    <h3>Informações Médicas</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                      {patientData.medicalConditions && (
                        <div className="ns-medical-info-item">
                          <span className="ns-medical-info-label">Condições Médicas</span>
                          <span className="ns-medical-info-value">{patientData.medicalConditions}</span>
                        </div>
                      )}
                      {patientData.allergies && (
                        <div className="ns-medical-info-item">
                          <span className="ns-medical-info-label">Alergias</span>
                          <span className="ns-medical-info-value">{patientData.allergies}</span>
                        </div>
                      )}
                      {patientData.medications && (
                        <div className="ns-medical-info-item">
                          <span className="ns-medical-info-label">Medicamentos</span>
                          <span className="ns-medical-info-value">{patientData.medications}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Observações */}
                {patientData.initialObservations && (
                  <div className="ns-patient-info-section">
                    <h3>Observações</h3>
                    <p className="ns-patient-observations">
                      {patientData.initialObservations}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="ns-patient-empty">
                <p>Nenhum dado encontrado.</p>
              </div>
            )}
          </Card>
          </div>
        )}
      </div>

      {/* Modal de Seleção de Plano */}
      <Modal
        isOpen={showPlanModal}
        onClose={() => {
          setShowPlanModal(false);
          setSelectedPlan(null);
          setPdfMessage('');
        }}
        title="Enviar PDF do Plano Alimentar"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowPlanModal(false);
                setSelectedPlan(null);
                setPdfMessage('');
              }}
              disabled={sendingPDF}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSendPlanPDF}
              disabled={!selectedPlan || sendingPDF}
            >
              {sendingPDF ? 'Enviando...' : 'Enviar PDF'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {loadingPlanos ? (
            <p>Carregando planos...</p>
          ) : planos.length === 0 ? (
            <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '20px' }}>
              Nenhum plano alimentar encontrado para este paciente.
            </p>
          ) : (
            <>
              <div>
                <label className="ns-field">
                  <span>Selecione o Plano *</span>
                  <select
                    className="ns-input"
                    value={selectedPlan || ''}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                  >
                    <option value="">Selecione um plano</option>
                    {planos.map((plano) => (
                      <option key={plano.id} value={plano.id}>
                        {plano.nome} - {new Date(plano.atualizadoEm).toLocaleDateString('pt-BR')}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div>
                <label className="ns-field">
                  <span>Mensagem Personalizada (Opcional)</span>
                  <textarea
                    className="ns-textarea"
                    rows={3}
                    placeholder="Adicione uma mensagem personalizada para o paciente..."
                    value={pdfMessage}
                    onChange={(e) => setPdfMessage(e.target.value)}
                  />
                </label>
                <p style={{ fontSize: '12px', color: theme.colors.textMuted, marginTop: '4px' }}>
                  Esta mensagem será enviada junto com o PDF no chat.
                </p>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Modal de Gravação de Vídeo */}
      <VideoRecorder
        isOpen={showVideoRecorder}
        onClose={() => setShowVideoRecorder(false)}
        onRecordComplete={handleVideoRecordComplete}
        maxDuration={300}
      />

      {/* Modal de Transmissão Ao Vivo */}
      <LiveStream
        isOpen={showLiveStream}
        onClose={() => setShowLiveStream(false)}
      />
    </div>
  );
};
